import { Cart, formatMoney, initMedia, openDrawer } from 'bb-theme';

const S = (window.BB && window.BB.strings) || {};
const fill = (str, key, val) => (str || '').replace(`[${key}]`, val);
// Mirrors Shopify's automatic discount « Pod : 2e à -50 % » (Achetez 1, obtenez 1 à -50 %, répété) :
// the discounted line is rounded half-up to the cent (vérifié : 59,99 € + 30,00 € = 89,99 €).
const keptPrice = (unit, pct) => Math.round((unit * (100 - (pct || 0))) / 100);
const tierTotal = (unit, q, pct) => unit * q - Math.floor(q / 2) * (unit - keptPrice(unit, pct));

class BBBuybox extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = '1';
    const json = this.querySelector('[data-pdp-json]');
    this.data = JSON.parse(json.textContent);
    this.form = this.querySelector('[data-pdp-form]');
    this.idInput = this.querySelector('[data-pdp-id]');
    this.qtyHidden = this.querySelector('[data-pdp-qty]');
    this.fieldsets = [...this.querySelectorAll('.opt[data-opt-index]')];
    this.selected = this.fieldsets.map((fs) => fs.querySelector('[aria-pressed="true"]')?.dataset.optValue);
    if (this.idInput) this.idInput.disabled = false;
    this.variant = this.findVariant(this.selected) || this.data.variants.find((v) => v.id === Number(this.idInput?.value)) || this.data.variants[0];
    this.pickWrap = this.querySelector('[data-tier-pick-wrap]');
    this.picks = {};
    if (this.pickWrap) {
      this.pickWrap.querySelectorAll('[data-pick-row]').forEach((row) => {
        const n = Number(row.dataset.pickRow);
        if (n < 2) return;
        this.picks[n] = [...(this.variant ? this.variant.options : [])];
        row.querySelectorAll('[data-pick-opt]').forEach((sel) => {
          sel.addEventListener('change', () => this.pickChange(n, Number(sel.dataset.pickOpt), sel.value));
        });
      });
    }

    this.fieldsets.forEach((fs, idx) => {
      fs.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-opt-value]');
        if (!btn || btn.disabled) return;
        this.selectOption(idx, btn.dataset.optValue);
      });
    });
    this.querySelectorAll('[data-tier]').forEach((r) => r.addEventListener('change', () => this.render()));
    this.querySelectorAll('[data-qty-step]').forEach((b) => b.addEventListener('click', () => {
      const input = this.querySelector('[data-qty-input]');
      const max = Number(input.max) || 99;
      input.value = Math.min(max, Math.max(1, Number(input.value) + Number(b.dataset.qtyStep)));
      this.render();
    }));
    this.querySelector('[data-qty-input]')?.addEventListener('change', (e) => {
      const max = Number(e.target.max) || 99;
      e.target.value = Math.min(max, Math.max(1, Math.round(Number(e.target.value) || 1)));
      this.render();
    });
    if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
    this.initGallery();
    this.render(true);
  }

  findVariant(opts) {
    if (!opts || opts.some((o) => o === undefined)) return null;
    return this.data.variants.find((v) => v.options.every((o, i) => o === opts[i])) || null;
  }

  selectOption(idx, value) {
    const next = [...this.selected];
    next[idx] = value;
    let v = this.findVariant(next);
    if (!v || !v.available) {
      const score = (x) => x.options.reduce((n, o, i) => n + (o === next[i] ? 1 : 0), 0);
      const best = this.data.variants
        .filter((x) => x.available && x.options[idx] === value)
        .sort((a, b) => score(b) - score(a))[0];
      if (best) {
        v = best;
        next.splice(0, next.length, ...best.options);
      }
    }
    this.selected = next;
    this.variant = v;
    this.render();
    if (v && v.media) this.goToMedia(v.media);
  }

  pickChange(n, idx, value) {
    const next = [...(this.picks[n] || [])];
    next[idx] = value;
    const v = this.findVariant(next);
    if (!v || !v.available) {
      const score = (x) => x.options.reduce((c, o, i) => c + (o === next[i] ? 1 : 0), 0);
      const best = this.data.variants
        .filter((x) => x.available && x.options[idx] === value)
        .sort((a, b) => score(b) - score(a))[0];
      if (best) next.splice(0, next.length, ...best.options);
    }
    this.picks[n] = next;
    this.render();
  }

  discountTotal(prices, pct) {
    const sorted = [...prices].sort((a, b) => b - a);
    const k = Math.floor(sorted.length / 2);
    const sum = sorted.reduce((a, b) => a + b, 0);
    const off = sorted.slice(sorted.length - k).reduce((a, p) => a + (p - keptPrice(p, pct)), 0);
    return sum - off;
  }

  currentTier() {
    const r = this.querySelector('[data-tier]:checked');
    if (!r) return null;
    return {
      el: r,
      type: r.dataset.tierType,
      qty: Number(r.dataset.tierQty) || 1,
      pct: Number(r.dataset.tierPct) || 0,
      pick: r.dataset.tierPick === 'true',
      variantId: r.dataset.tierVariant ? Number(r.dataset.tierVariant) : null,
      available: r.dataset.tierAvailable !== 'false',
      price: Number(r.dataset.tierPrice),
      compare: Number(r.dataset.tierCompare)
    };
  }

  lineItem() {
    const v = this.variant;
    const stepper = Number(this.querySelector('[data-qty-input]')?.value) || 1;
    const t = this.currentTier();
    if (t && t.type !== 'qty') {
      const q0 = t.qty * stepper;
      return { items: [{ id: t.variantId, quantity: q0 }], id: t.variantId, quantity: q0, total: t.price * stepper, available: t.available };
    }
    const q = t ? t.qty : 1;
    const usePick = !!(t && t.pick && q > 1 && this.pickWrap);
    const variants = [];
    for (let i = 1; i <= q; i += 1) variants.push(i === 1 || !usePick ? v : this.findVariant(this.picks[i] || []));
    const prices = [];
    variants.forEach((x) => { for (let k = 0; k < stepper; k += 1) prices.push(x ? x.price : 0); });
    const map = new Map();
    variants.forEach((x) => { if (x) map.set(x.id, (map.get(x.id) || 0) + stepper); });
    return {
      items: [...map].map(([id, quantity]) => ({ id, quantity })),
      id: v ? v.id : null,
      quantity: q * stepper,
      total: this.discountTotal(prices, t ? t.pct : 0),
      available: variants.every((x) => x && x.available)
    };
  }

  render(initial) {
    const v = this.variant;
    // Option buttons: selected state + availability given the other selections
    this.fieldsets.forEach((fs, idx) => {
      const label = fs.querySelector('[data-opt-selected]');
      if (label) label.textContent = this.selected[idx] || '';
      fs.querySelectorAll('[data-opt-value]').forEach((btn) => {
        const val = btn.dataset.optValue;
        const pressed = this.selected[idx] === val;
        btn.setAttribute('aria-pressed', String(pressed));
        const combo = [...this.selected];
        combo[idx] = val;
        const exact = this.findVariant(combo);
        const exactOk = !!(exact && exact.available);
        const anyOk = this.data.variants.some((x) => x.available && x.options[idx] === val);
        btn.toggleAttribute('data-unavailable', !exactOk);
        btn.disabled = !exactOk;
        btn.setAttribute('aria-disabled', String(!exactOk));
        const so = btn.querySelector('[data-opt-so]');
        if (so) {
          so.hidden = exactOk;
          if (!exactOk) {
            const other = this.selected.filter((_, i) => i !== idx).join(' / ');
            so.textContent = anyOk && other ? fill(S.soldOutIn, 'value', other) : S.unavailable;
            if (btn.classList.contains('swatch')) so.textContent = ` — ${so.textContent}`;
          }
        }
      });
    });

    // Price block
    const set = (sel, fn) => this.querySelectorAll(sel).forEach(fn);
    if (v) {
      const onSale = v.compare > v.price;
      set('[data-pdp-price]', (el) => { el.textContent = v.price_f; });
      set('[data-pdp-compare]', (el) => {
        el.hidden = !onSale;
        if (onSale) el.lastChild.textContent = v.compare_f;
      });
      set('[data-pdp-save]', (el) => { el.hidden = !onSale; if (onSale) el.textContent = fill(S.save, 'amount', v.save_f); });
      set('[data-pdp-save-badge]', (el) => { el.hidden = !onSale; if (onSale) el.textContent = fill(S.save, 'amount', v.save_f).toUpperCase(); });
      set('[data-pdp-unit]', (el) => { el.hidden = !v.unit_f; el.textContent = v.unit_f || ''; });
      set('[data-pdp-sku]', (el) => { el.textContent = v.sku || ''; });
      set('[data-pdp-stock]', (el) => {
        const show = typeof v.inv === 'number' && v.inv > 0 && v.inv <= this.data.invThreshold;
        el.hidden = !show;
        if (show) el.querySelector('[data-pdp-stock-text]').textContent = fill(S.lowStock, 'count', v.inv);
      });
    }

    // Tier prices for "quantity of selected variant" tiers follow the variant
    this.querySelectorAll('[data-tier]').forEach((r) => {
      const card = r.closest('.tier');
      if (r.dataset.tierType === 'qty' && v) {
        const q = Number(r.dataset.tierQty) || 1;
        const total = tierTotal(v.price, q, Number(r.dataset.tierPct));
        const cmpUnit = Math.max(v.compare || v.price, v.price);
        card.querySelector('[data-tier-price-out]').textContent = formatMoney(total);
        const c = card.querySelector('[data-tier-compare-out]');
        c.hidden = cmpUnit * q <= total;
        c.textContent = formatMoney(cmpUnit * q);
        const d = card.querySelector('[data-tier-detail]');
        if (d) d.textContent = d.dataset.tierDetail.replace('[each]', formatMoney(Math.floor(total / q))).replace('[save]', formatMoney(cmpUnit * q - total));
        card.classList.toggle('is-unavailable', !v.available);
      } else {
        card.classList.toggle('is-unavailable', r.dataset.tierAvailable === 'false');
      }
    });

    // Per-item pickers (each pod its own colour / size)
    if (this.pickWrap) {
      const tp = this.currentTier();
      const show = !!(tp && tp.pick && tp.type === 'qty' && tp.qty > 1);
      this.pickWrap.hidden = !show;
      this.pickWrap.querySelectorAll('[data-pick-row]').forEach((row) => {
        const n = Number(row.dataset.pickRow);
        row.hidden = !show || n > (tp ? tp.qty : 1);
        if (n === 1) {
          const m = row.querySelector('[data-pick-main]');
          if (m && v) m.textContent = v.title;
          return;
        }
        const sel = this.picks[n] || [];
        row.querySelectorAll('[data-pick-opt]').forEach((box) => {
          const idx = Number(box.dataset.pickOpt);
          if (sel[idx] !== undefined) box.value = sel[idx];
          [...box.options].forEach((o) => {
            const combo = [...sel];
            combo[idx] = o.value;
            const x = this.findVariant(combo);
            const ok = !!(x && x.available);
            const anyOk = this.data.variants.some((y) => y.available && y.options[idx] === o.value);
            o.disabled = !anyOk;
            o.textContent = ok ? o.value : `${o.value} — ${S.soldOut}`;
          });
        });
      });
    }

    // Form + button
    const li = this.lineItem();
    if (this.idInput && li.id) this.idInput.value = li.id;
    if (this.qtyHidden) this.qtyHidden.value = li.quantity;
    const atc = this.querySelector('[data-pdp-atc]');
    if (atc) {
      const label = atc.querySelector('[data-pdp-atc-label]');
      atc.disabled = !li.available || !li.id;
      atc.setAttribute('aria-disabled', String(atc.disabled));
      if (!v && !li.id) label.textContent = S.unavailable;
      else if (!li.available) label.textContent = S.soldOut;
      else label.textContent = this.data.showTotal ? fill(S.addToCartTotal, 'total', formatMoney(li.total)) : S.addToCart;
    }

    if (!initial && v && this.dataset.url && window.location.pathname.includes('/products/')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
    }
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: v, line: li } }));
  }

  async onSubmit(e) {
    e.preventDefault();
    const li = this.lineItem();
    const btn = this.querySelector('[data-pdp-atc]');
    const msg = this.querySelector('[data-pdp-msg]');
    if (!li.id || !li.available) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    msg.textContent = '';
    msg.classList.remove('is-error');
    try {
      await Cart.add(li.items, btn);
      msg.textContent = S.added || '';
    } catch (err) {
      msg.textContent = err.message;
      msg.classList.add('is-error');
    } finally {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }
  }

  /* ---------- Gallery ---------- */
  initGallery() {
    this.track = this.querySelector('[data-gal-track]');
    if (!this.track) return;
    this.slides = [...this.track.children];
    this.thumbs = [...this.querySelectorAll('[data-gal-go]')];
    this.dots = [...this.querySelectorAll('.gal__dot')];
    this.index = 0;
    let raf;
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (i !== this.index) this.setActive(i);
      });
    }, { passive: true });
    this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.galGo))));
    this.querySelector('[data-gal-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-gal-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
    });
    const lb = this.closest('section')?.querySelector('[data-lightbox]');
    if (lb) {
      const lbTrack = lb.querySelector('[data-lb-track]');
      this.querySelectorAll('[data-gal-zoom]').forEach((z) => z.addEventListener('click', () => {
        openDrawer(lb, z);
        const target = lbTrack.querySelector(`[data-lb-index="${z.dataset.galZoom}"]`);
        requestAnimationFrame(() => { if (target) lbTrack.scrollTo({ left: target.offsetLeft, behavior: 'instant' }); });
      }));
      const step = (d) => lbTrack.scrollBy({ left: d * lbTrack.clientWidth, behavior: 'smooth' });
      lb.querySelector('[data-lb-prev]')?.addEventListener('click', () => step(-1));
      lb.querySelector('[data-lb-next]')?.addEventListener('click', () => step(1));
      lb.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') step(1);
        if (e.key === 'ArrowLeft') step(-1);
      });
    }
  }

  setActive(i) {
    this.index = i;
    this.thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
    this.dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
    this.slides.forEach((s, n) => {
      if (n !== i) s.querySelectorAll('video').forEach((vid) => vid.pause());
    });
  }

  go(i) {
    if (!this.slides || !this.slides.length) return;
    const n = (i + this.slides.length) % this.slides.length;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    this.track.scrollTo({ left: this.slides[n].offsetLeft, behavior });
    this.setActive(n);
  }

  goToMedia(mediaId) {
    if (!this.slides) return;
    const i = this.slides.findIndex((s) => Number(s.dataset.mediaId) === Number(mediaId));
    if (i > -1) this.go(i);
  }
}

if (!customElements.get('bb-buybox')) customElements.define('bb-buybox', BBBuybox);
document.addEventListener('shopify:section:load', (e) => initMedia(e.target));
